import React, { useContext, useEffect, useState } from 'react';
import { Modal, TouchableOpacity, View } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MyAppText from './MyAppText';
import { ChangeLanguage, getDefaultLanguage, translate } from '../services/translate';
import { faCircleHalfStroke, faGear, faLanguage, faRightFromBracket } from '@fortawesome/free-solid-svg-icons';
import app from '../services/version.json';
import { centralAPI } from '../services/central-api';
import { EdgeInsets, useSafeAreaInsets } from 'react-native-safe-area-context';
import ReportAbuseModal from '../pages/ReportAbuseModal';
import { useAuth } from '../context/Auth';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import ThemeContext from '../context/Theme';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { RootNavigation } from '../Routes';
import { forceRedirectToLogin } from '../services/navigation-assistant';
import moment from 'moment';

interface UserInformationParameters {
    navigation: RootNavigation;
    showDropDown: boolean;
    setShowDropDown: (value: boolean) => void;
}

export default function UserInformationModal({ navigation, showDropDown, setShowDropDown }: UserInformationParameters) {
    const { toggleTheme } = useContext(ThemeContext);
    const { user: authenticatedUser } = useAuth();
    const { styles, theme } = useStyles(styleSheet);
    const [language, setLanguage] = useState<string>('');
    const [isReportAbuseModalVisible, setReportAbuseModalVisible] = useState(false);
    const insets = useSafeAreaInsets();
    const [nativeVersion, setNativeVersion] = useState<string | null | undefined>(undefined);

    async function getLanguage() {
        const language = getDefaultLanguage();
        setLanguage(language);
    }

    async function getNativeVersion() {
        const nativeVersion = await AsyncStorage.getItem('NATIVE_VERSION');
        setNativeVersion(nativeVersion);
    }

    useEffect(() => {
        if (showDropDown) {
            getNativeVersion();
        }
    }, [showDropDown]);

    useEffect(() => {
        getLanguage();
    }, []);

    if (!authenticatedUser) {
        return <></>;
    }

    const daysSincePasswordReset = authenticatedUser
        ? moment().diff(authenticatedUser.passwordResetDate, 'days')
        : 0;

    const passwordValid =
        !!authenticatedUser &&
        (
            authenticatedUser.passwordRotationDays <= 0 ||
            daysSincePasswordReset < authenticatedUser.passwordRotationDays
        );

    const userAllowed = !!authenticatedUser && authenticatedUser.acceptedTermsOfUse && passwordValid;

    return (
        <>
            <Modal visible={showDropDown} animationType='none' onRequestClose={() => setShowDropDown(false)} transparent={true}>
                <TouchableOpacity onPress={() => setShowDropDown(false)} style={styles.modalContainer}></TouchableOpacity>
                <View style={styles.modalBody(insets)}>
                    <View style={styles.optionsBody}>
                        <TouchableOpacity
                            onPress={() => {
                                toggleTheme();
                            }}
                            style={styles.modalButtons}
                        >
                            <FontAwesomeIcon icon={faCircleHalfStroke} color={theme.colors.iconColor} style={{ width: 20 }} />
                            <MyAppText>{
                                translate((() => {
                                    if (UnistylesRuntime.themeName === 'light') {
                                        return 'changeToLightAlt';
                                    }
                                    if (UnistylesRuntime.themeName === 'lightAlt') {
                                        return 'changeToDark';
                                    }
                                    return 'changeToLight';
                                })())
                            }
                            </MyAppText>
                        </TouchableOpacity>
                        <View style={styles.tableLine}></View>
                        <TouchableOpacity
                            onPress={async () => {
                                if (language == 'pt-br') {
                                    setLanguage('en-us');
                                    await ChangeLanguage('en-us');
                                } else {
                                    setLanguage('pt-br');
                                    await ChangeLanguage('pt-br');
                                }
                            }}
                            style={styles.modalButtons}
                        >
                            <FontAwesomeIcon icon={faLanguage} color={theme.colors.iconColor} style={{ width: 20 }} />
                            <MyAppText>{translate('changeLanguage')}: {translate(language)}</MyAppText>
                        </TouchableOpacity>
                        <View style={styles.tableLine}></View>
                        {userAllowed ? <>
                            <TouchableOpacity
                                onPress={() => {
                                    navigation.navigate('User', { screen: 'Account' });
                                    setShowDropDown(false);
                                }}
                                style={styles.modalButtons}
                            >
                                <FontAwesomeIcon icon={faGear} color={theme.colors.iconColor} style={{ width: 20 }} />
                                <MyAppText>{translate('settings')}</MyAppText>
                            </TouchableOpacity>
                            <View style={styles.tableLine}></View>
                            <TouchableOpacity onPress={async () => {
                                navigation.navigate('ChangeLogs');
                                setShowDropDown(false);
                            }} style={{ justifyContent: 'center', gap: 10 }}>
                                {!nativeVersion ?
                                    <MyAppText>{translate('version')}: {app[0].version}</MyAppText>
                                    : <>
                                        <MyAppText>{translate('webVersion')}: {app[0].version}</MyAppText>
                                        <MyAppText>{translate('nativeVersion')}: {nativeVersion}</MyAppText>
                                    </>
                                }
                            </TouchableOpacity>
                            <View style={styles.tableLine}></View>
                        </> : null
                        }
                        <TouchableOpacity
                            onPress={async () => {
                                try {
                                    await centralAPI.logout();
                                    setShowDropDown(false);
                                    forceRedirectToLogin();
                                } catch (err) {
                                    console.error(err);
                                }
                            }}
                            style={styles.modalButtons}
                        >
                            <FontAwesomeIcon icon={faRightFromBracket} color={'rgb(240, 54, 103)'} style={{ width: 20 }} />
                            <MyAppText style={styles.logoutText}>{translate('logout')}</MyAppText>
                        </TouchableOpacity>
                        <View style={styles.tableLine}></View>
                    </View>
                    <View style={{ padding: 10, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', columnGap: 15 }}>
                        <TouchableOpacity
                            disabled={!userAllowed}
                            onPress={() => {
                                navigation.navigate('TermsOfUse');
                                setShowDropDown(false);
                            }}>
                            <MyAppText style={styles.footerText}>{translate('termsOfUse')}</MyAppText>
                        </TouchableOpacity>
                        <TouchableOpacity
                            disabled={!authenticatedUser.permissions.create_support_ticket || !userAllowed}
                            onPress={() => {
                                navigation.navigate('ContactUs');
                                setShowDropDown(false);
                            }}>
                            <MyAppText style={styles.footerText}>{translate('contactUs')}</MyAppText>
                        </TouchableOpacity>
                    </View>
                    <View style={{ paddingBottom: 10, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', columnGap: 15 }}>
                        <TouchableOpacity
                            disabled={!userAllowed}
                            onPress={() => {
                                navigation.navigate('FrequentlyAskedQuestions');
                                setShowDropDown(false);
                            }}>
                            <MyAppText style={styles.footerText}>FAQ</MyAppText>
                        </TouchableOpacity>
                        <TouchableOpacity
                            disabled={!userAllowed}
                            onPress={() => {
                                setShowDropDown(false);
                                setReportAbuseModalVisible(true);
                            }}>
                            <MyAppText style={styles.footerText}>{translate('reportAbuse')}</MyAppText>
                        </TouchableOpacity>
                    </View>
                </View>
            </Modal>
            <ReportAbuseModal
                isModalVisible={isReportAbuseModalVisible}
                onClose={() => setReportAbuseModalVisible(false)}
                authenticatedUser={authenticatedUser}
            />
        </>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    footerText: {
        fontSize: 13,
    },
    modalContainer: {
        flexDirection: 'column',
        flex: 1
    },
    modalBody: (insets: EdgeInsets) => {
        return {
            backgroundColor: theme.colors.card.background,
            borderColor: theme.colors.card.border,
            borderWidth: 1,
            flexDirection: 'column',
            borderRadius: 4,
            paddingHorizontal: 20,
            paddingTop: 20,
            position: 'absolute',
            top: 55 + insets.top,
            right: 5 + insets.right,
            minWidth: 300
        };
    },
    optionsBody: {
        rowGap: 15,
    },
    modalButtons: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10
    },
    tableLine: {
        borderBottomColor: theme.colors.card.border,
        borderBottomWidth: 1,
    },
    logoutText: {
        color: 'rgb(240, 54, 103)',
        fontSize: 15,
    },
}));
